/**
 * Execution Mode Selector Component
 *
 * Allows selecting between direct, simple, triad, and full execution modes.
 */
import { clsx } from 'clsx'
import {
  BoltIcon,
  CpuChipIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { AgentExecutionMode } from '@/types/unified'

interface ExecutionModeSelectorProps {
  value: AgentExecutionMode
  onChange: (mode: AgentExecutionMode) => void
  disabled?: boolean
  compact?: boolean
  showDescription?: boolean
  className?: string
}

const executionModeConfig = {
  [AgentExecutionMode.DIRECT]: {
    label: 'Direct',
    shortLabel: 'Direct',
    description: 'Single LLM call - fastest, minimal overhead',
    features: ['Single call', 'Fastest', 'No review'],
    icon: BoltIcon,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-900/30',
    borderColor: 'border-cyan-600',
    complexity: 'Low',
  },
  [AgentExecutionMode.SIMPLE]: {
    label: 'Simple',
    shortLabel: 'Simple',
    description: 'Specialist agent only - balanced speed and quality',
    features: ['Specialist', 'Fast', 'Basic'],
    icon: CpuChipIcon,
    color: 'text-green-400',
    bgColor: 'bg-green-900/30',
    borderColor: 'border-green-600',
    complexity: 'Low-Medium',
  },
  [AgentExecutionMode.TRIAD]: {
    label: 'Triad',
    shortLabel: 'Triad',
    description: 'Manager-Worker-Validator pattern for quality output',
    features: ['Manager', 'Worker', 'Validator'],
    icon: UserGroupIcon,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-900/30',
    borderColor: 'border-yellow-600',
    complexity: 'Medium',
  },
  [AgentExecutionMode.FULL]: {
    label: 'Full',
    shortLabel: 'Full',
    description: 'Triad + QA review + automatic revision loop',
    features: ['Triad', 'QA Review', 'Revisions'],
    icon: ShieldCheckIcon,
    color: 'text-purple-400',
    bgColor: 'bg-purple-900/30',
    borderColor: 'border-purple-600',
    complexity: 'High',
  },
}

// Visual representation of the pipeline
interface PipelineStage {
  label: string
  color: string
  hasLoop?: boolean
}

function PipelineVisualization({ mode }: { mode: AgentExecutionMode }) {
  const stages: Record<AgentExecutionMode, PipelineStage[]> = {
    [AgentExecutionMode.DIRECT]: [
      { label: 'Input', color: 'bg-gray-600' },
      { label: 'LLM', color: 'bg-cyan-600' },
      { label: 'Output', color: 'bg-gray-600' },
    ],
    [AgentExecutionMode.SIMPLE]: [
      { label: 'Input', color: 'bg-gray-600' },
      { label: 'Specialist', color: 'bg-green-600' },
      { label: 'Output', color: 'bg-gray-600' },
    ],
    [AgentExecutionMode.TRIAD]: [
      { label: 'Manager', color: 'bg-yellow-600' },
      { label: 'Worker', color: 'bg-yellow-500' },
      { label: 'Validator', color: 'bg-yellow-600' },
    ],
    [AgentExecutionMode.FULL]: [
      { label: 'Triad', color: 'bg-purple-600' },
      { label: 'QA', color: 'bg-purple-500' },
      { label: 'Revise', color: 'bg-purple-400', hasLoop: true },
    ],
  }

  return (
    <div className="flex items-center gap-1 mt-2">
      {stages[mode].map((stage, idx) => (
        <div key={idx} className="flex items-center">
          <div
            className={clsx(
              'px-2 py-0.5 rounded text-xs font-medium text-white',
              stage.color
            )}
          >
            {stage.label}
          </div>
          {idx < stages[mode].length - 1 && (
            <ArrowPathIcon
              className={clsx(
                'w-3 h-3 mx-0.5',
                stage.hasLoop ? 'text-purple-400' : 'text-gray-500'
              )}
            />
          )}
        </div>
      ))}
    </div>
  )
}

export default function ExecutionModeSelector({
  value,
  onChange,
  disabled = false,
  compact = false,
  showDescription = true,
  className,
}: ExecutionModeSelectorProps) {
  const modes = Object.values(AgentExecutionMode)

  if (compact) {
    return (
      <div className={clsx('flex rounded-lg bg-gray-800 p-1', className)}>
        {modes.map((mode) => {
          const config = executionModeConfig[mode]
          const Icon = config.icon
          const isSelected = value === mode

          return (
            <button
              key={mode}
              onClick={() => onChange(mode)}
              disabled={disabled}
              title={config.description}
              className={clsx(
                'flex items-center px-3 py-1.5 text-sm rounded-md transition-colors',
                isSelected
                  ? `${config.bgColor} ${config.color} border ${config.borderColor}`
                  : 'text-gray-400 hover:text-white hover:bg-gray-700',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Icon className="w-4 h-4 mr-1.5" />
              {config.shortLabel}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div className={clsx('space-y-3', className)}>
      <label className="text-sm font-medium text-gray-300">Execution Mode</label>

      <div className="grid grid-cols-2 gap-3">
        {modes.map((mode) => {
          const config = executionModeConfig[mode]
          const Icon = config.icon
          const isSelected = value === mode

          return (
            <button
              key={mode}
              onClick={() => onChange(mode)}
              disabled={disabled}
              className={clsx(
                'flex flex-col items-start p-4 rounded-lg border-2 transition-all text-left',
                isSelected
                  ? `${config.bgColor} ${config.borderColor}`
                  : 'bg-gray-800 border-gray-700 hover:border-gray-600',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="flex items-center justify-between w-full mb-1">
                <div className="flex items-center">
                  <Icon className={clsx('w-5 h-5 mr-2', isSelected ? config.color : 'text-gray-400')} />
                  <span className={clsx('font-medium', isSelected ? 'text-white' : 'text-gray-300')}>
                    {config.label}
                  </span>
                </div>
                <span className="text-xs text-gray-500">{config.complexity}</span>
              </div>

              {showDescription && (
                <p className="text-xs text-gray-400 mb-2">{config.description}</p>
              )}

              {/* Feature tags */}
              <div className="flex flex-wrap gap-1 mb-2">
                {config.features.map((feature, idx) => (
                  <span
                    key={idx}
                    className={clsx(
                      'text-xs px-1.5 py-0.5 rounded',
                      isSelected ? `${config.bgColor} ${config.color}` : 'bg-gray-700 text-gray-400'
                    )}
                  >
                    {feature}
                  </span>
                ))}
              </div>

              {/* Pipeline visualization */}
              <PipelineVisualization mode={mode} />
            </button>
          )
        })}
      </div>
    </div>
  )
}
